// Minimal, dependency-free replacement for the slice of @tanstack/react-query
// this app uses. Implements: QueryClient, QueryClientProvider, useQueryClient,
// useQuery, useMutation. Zero third-party deps — only React.
//
// Why this exists: the @tanstack/* namespace suffered an npm supply-chain
// compromise (CVE-2026-45321, 2026-05-11). @tanstack/react-query itself was
// confirmed clean, but to remove all TanStack surface from this internal
// review tool we hand-rolled the tiny bit we actually need.
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

type QueryKey = readonly unknown[];

function keyHash(key: QueryKey): string {
  return JSON.stringify(key);
}

// Does an invalidation key (prefix) match a query's key?
// e.g. invalidate ['concepts'] should refetch ['concepts', 'C'].
function keyMatchesPrefix(queryKey: QueryKey, prefix: QueryKey): boolean {
  if (prefix.length > queryKey.length) return false;
  for (let i = 0; i < prefix.length; i++) {
    if (JSON.stringify(queryKey[i]) !== JSON.stringify(prefix[i])) return false;
  }
  return true;
}

interface CacheEntry {
  data: unknown;
  ts: number;
}

export class QueryClient {
  // Accept (and ignore) a react-query-style config for drop-in compatibility.
  // This shim is single-attempt (no retry) and caches until invalidated.
  constructor(_config?: unknown) {}

  private cache = new Map<string, CacheEntry>();
  // listeners registered per active useQuery so invalidate can trigger refetch
  private listeners = new Set<{ queryKey: QueryKey; refetch: () => void }>();

  getCached(key: QueryKey): CacheEntry | undefined {
    return this.cache.get(keyHash(key));
  }

  setCached(key: QueryKey, data: unknown): void {
    this.cache.set(keyHash(key), { data, ts: Date.now() });
  }

  subscribe(entry: { queryKey: QueryKey; refetch: () => void }): () => void {
    this.listeners.add(entry);
    return () => {
      this.listeners.delete(entry);
    };
  }

  invalidateQueries(opts: { queryKey: QueryKey }): void {
    const prefix = opts.queryKey;
    // drop matching cache entries
    for (const k of Array.from(this.cache.keys())) {
      try {
        const parsed = JSON.parse(k) as QueryKey;
        if (keyMatchesPrefix(parsed, prefix)) this.cache.delete(k);
      } catch {
        /* ignore non-array keys */
      }
    }
    // trigger refetch on any mounted query whose key matches
    for (const l of this.listeners) {
      if (keyMatchesPrefix(l.queryKey, prefix)) l.refetch();
    }
  }

  clear(): void {
    this.cache.clear();
  }
}

const QueryClientContext = createContext<QueryClient | null>(null);

export function QueryClientProvider({ client, children }: { client: QueryClient; children: React.ReactNode }) {
  return <QueryClientContext.Provider value={client}>{children}</QueryClientContext.Provider>;
}

export function useQueryClient(): QueryClient {
  const c = useContext(QueryClientContext);
  if (!c) throw new Error('useQueryClient must be used within a QueryClientProvider');
  return c;
}

export interface UseQueryResult<T> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

export function useQuery<T>(opts: {
  queryKey: QueryKey;
  queryFn: () => Promise<T>;
  enabled?: boolean;
}): UseQueryResult<T> {
  const client = useQueryClient();
  const enabled = opts.enabled !== false;
  const hash = keyHash(opts.queryKey);

  const cached = client.getCached(opts.queryKey);
  const [data, setData] = useState<T | undefined>(cached ? (cached.data as T) : undefined);
  const [isLoading, setIsLoading] = useState<boolean>(enabled && !cached);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // keep latest queryFn without forcing effect re-runs
  const fnRef = useRef(opts.queryFn);
  fnRef.current = opts.queryFn;
  const reqId = useRef(0);

  const run = useCallback(() => {
    if (!enabled) return;
    const id = ++reqId.current;
    setIsLoading(true);
    setIsError(false);
    setError(null);
    fnRef.current()
      .then((res) => {
        if (id !== reqId.current) return; // stale
        client.setCached(opts.queryKey, res);
        setData(res);
        setIsLoading(false);
      })
      .catch((e) => {
        if (id !== reqId.current) return;
        setIsError(true);
        setError(e);
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, enabled, hash]);

  // fetch on mount / when key or enabled changes
  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    const c = client.getCached(opts.queryKey);
    if (c) {
      setData(c.data as T);
      setIsLoading(false);
    } else {
      run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, enabled]);

  // subscribe to invalidations
  useEffect(() => {
    const sub = { queryKey: opts.queryKey, refetch: run };
    return client.subscribe(sub);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, hash, run]);

  return { data, isLoading, isError, error, refetch: run };
}

export interface UseMutationResult<TArgs> {
  mutate: (args: TArgs) => void;
  mutateAsync: (args: TArgs) => Promise<unknown>;
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: unknown;
}

export function useMutation<TArgs = void, TData = unknown>(opts: {
  mutationFn: (args: TArgs) => Promise<TData>;
  onSuccess?: (data: TData, args: TArgs) => void;
  onError?: (error: unknown, args: TArgs) => void;
}): UseMutationResult<TArgs> {
  const [isPending, setIsPending] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const optsRef = useRef(opts);
  optsRef.current = opts;

  const mutateAsync = useCallback((args: TArgs) => {
    setIsPending(true);
    setIsError(false);
    setIsSuccess(false);
    setError(null);
    return optsRef.current
      .mutationFn(args)
      .then((data) => {
        setIsPending(false);
        setIsSuccess(true);
        optsRef.current.onSuccess?.(data, args);
        return data;
      })
      .catch((e) => {
        setIsPending(false);
        setIsError(true);
        setError(e);
        optsRef.current.onError?.(e, args);
        throw e;
      });
  }, []);

  const mutate = useCallback(
    (args: TArgs) => {
      void mutateAsync(args).catch(() => {
        /* swallow: state already reflects error */
      });
    },
    [mutateAsync],
  );

  return { mutate, mutateAsync, isPending, isError, isSuccess, error };
}
